/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CertificateSelect.tsx
 🗓️ Created: 2025-12-24
 🧩 Description:
    Generic certificate selection component with search functionality.
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, Search, ShieldCheck } from 'lucide-react';

interface Certificate {
    id: number;
    name: string;
    commonName: string;
}

interface CertificateSelectProps {
    value: number | null | undefined;
    onChange: (value: number | null) => void;
    certificates: Certificate[];
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    error?: boolean;
    searchPlaceholder?: string;
    noResultsText?: string;
    noDataText?: string;
}

export const CertificateSelect: React.FC<CertificateSelectProps> = ({
    value,
    onChange,
    certificates,
    className = '',
    placeholder = 'Select certificate...',
    disabled = false,
    loading = false,
    error = false,
    searchPlaceholder = 'Search certificates...',
    noResultsText = 'No results found',
    noDataText = 'No certificates available'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const comboboxRef = useRef<HTMLDivElement>(null);

    // Close combobox when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter certificates based on search query
    const filteredCertificates = useMemo(() => {
        if (!searchQuery.trim()) {
            return certificates;
        }

        const query = searchQuery.toLowerCase();
        return certificates.filter(cert =>
            cert.name.toLowerCase().includes(query) ||
            cert.commonName.toLowerCase().includes(query)
        );
    }, [certificates, searchQuery]);

    // Get selected certificate display label
    const selectedCertLabel = useMemo(() => {
        if (!value) return null;
        const cert = certificates.find(c => c.id === value);
        return cert ? `${cert.name} (${cert.commonName})` : null;
    }, [value, certificates]);

    // Handle selection
    const handleSelect = (certValue: number | null) => {
        onChange(certValue);
        setIsOpen(false);
        setSearchQuery('');
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'Escape':
                setIsOpen(false);
                setSearchQuery('');
                break;
            case 'Enter':
                if (!isOpen) {
                    e.preventDefault();
                    setIsOpen(true);
                }
                break;
        }
    };

    return (
        <div className={`relative ${className}`} ref={comboboxRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                disabled={disabled || loading}
                className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${error ? 'border-red-500' : ''}
        `}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate text-left">
                        {loading
                            ? 'Loading...'
                            : selectedCertLabel || placeholder
                        }
                    </span>
                </div>
                <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-border">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto">
                        {/* None Option */}
                        <button
                            type="button"
                            onClick={() => handleSelect(null)}
                            className={`
                w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                flex items-center gap-2
                ${!value ? 'bg-primary/10 text-primary' : 'text-foreground'}
              `}
                        >
                            <ShieldCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="truncate italic text-muted-foreground">None</span>
                        </button>

                        {/* Certificate Options */}
                        {filteredCertificates.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                                {searchQuery ? noResultsText : noDataText}
                            </div>
                        ) : (
                            filteredCertificates.map((cert) => (
                                <button
                                    key={cert.id}
                                    type="button"
                                    onClick={() => handleSelect(cert.id)}
                                    className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === cert.id ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                                >
                                    <ShieldCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate">{cert.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{cert.commonName}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Footer with count */}
                    {certificates.length > 0 && (
                        <div className="px-3 py-2 border-t border-border bg-muted/30">
                            <p className="text-xs text-muted-foreground">
                                {filteredCertificates.length} of {certificates.length} {certificates.length === 1 ? 'certificate' : 'certificates'}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CertificateSelect;
